import React, { useEffect, useMemo, useState } from "react";
import { Container, Card, Row, Col, Form, Button, Badge, Spinner, Table, InputGroup, Modal } from "react-bootstrap";
import { useParams, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { fires } from "../../firebase";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";

export default function BusinessManagement() {
  const { currentUser } = useAuth();
  const { id: routeEventId } = useParams();
  const location = useLocation();
  const queryEventId = new URLSearchParams(location.search).get('id');
  const eventId = routeEventId || queryEventId || '';
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [savingId, setSavingId] = useState("");
  const [query, setQuery] = useState("");
  const [uploadingImageId, setUploadingImageId] = useState("");
  const [draggedPhotoIndex, setDraggedPhotoIndex] = useState(null);
  const [draggedPhotoBusinessId, setDraggedPhotoBusinessId] = useState(null);
  const [showPhotoSelector, setShowPhotoSelector] = useState({});

  const dayOrder = [
    'Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'
  ];
  const defaultWeeklyHours = useMemo(() => (
    dayOrder.map(day => ({ day, hours: '' }))
  ), []);

  const normalizeWeeklyHours = (raw) => {
    try {
      if (Array.isArray(raw)) {
        // Ensure all 7 days exist and keep order
        const map = new Map(raw.map(r => [String(r.day || '').trim(), { day: String(r.day || '').trim(), hours: String(r.hours || '') }]));
        return dayOrder.map(day => map.get(day) || { day, hours: '' });
      }
      if (raw && typeof raw === 'object') {
        // Legacy object format: { monday: {open, close, closed}, ... }
        const keyToDay = {
          monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday',
          friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday'
        };
        const arr = dayOrder.map(day => {
          const key = Object.keys(keyToDay).find(k => keyToDay[k] === day);
          const cfg = raw[key] || {};
          let hours = '';
          if (cfg.closed === true) {
            hours = 'Closed';
          } else if (cfg.open || cfg.close) {
            hours = `${cfg.open || ''} - ${cfg.close || ''}`.trim();
          }
          return { day, hours };
        });
        return arr;
      }
    } catch (e) {
      // fallthrough to default
    }
    return [...defaultWeeklyHours];
  };

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        if (!currentUser) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        const adminDoc = await fires.collection('admin').doc(currentUser.uid).get();
        if (!adminDoc.exists) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        // Allow any admin to access - no role restriction here
        setIsAdmin(true);
        setLoading(false);
        loadBusinesses();
      } catch (e) {
        console.error('Admin check failed', e);
        setError('Failed to verify admin access');
        setLoading(false);
      }
    };
    checkAdmin();
  }, [currentUser]);

  const normalizeServices = (raw) => {
    try {
      if (Array.isArray(raw)) {
        // Convert simple string array to object array, or prune to { id, name, images }
        if (raw.length > 0 && typeof raw[0] === 'string') {
          return raw.map((name, idx) => ({ id: `${Date.now()}-${idx}`, name: String(name), images: [] }));
        }
        // Already objects: keep only name/images
        return raw.map((s, idx) => ({ id: s.id || `${Date.now()}-${idx}`, name: s.name || '', images: Array.isArray(s.images) ? s.images : [] }));
      }
    } catch (e) {
      // fallthrough
    }
    return [];
  };

  const normalizePrices = (raw) => {
    try {
      if (raw && typeof raw === 'object') {
        // Convert map/object to array format for easier management
        return Object.entries(raw).map(([key, value]) => ({
          id: key,
          name: value.priceDescription || key,
          fees: {
            original: value.fees?.original || 0,
            display: value.fees?.display || 0,
            discount: value.fees?.discount || 0,
            tax: value.fees?.tax || 0
          },
          description: value.description || '',
          imageURL: value.imageURL || ''
        }));
      }
    } catch (e) {
      // fallthrough
    }
    return [];
  };

  const loadBusinesses = async () => {
    try {
      setError("");
      if (eventId) {
        const doc = await fires.collection('events').doc(eventId).get();
        if (!doc.exists) {
          setBusinesses([]);
          setError('Business not found');
          return;
        }
        const data = doc.data() || {};
        const descriptionMarkdown = data.description || '';
        setBusinesses([{
          id: doc.id,
          title: data.title || data.eventDetails?.title || 'Untitled',
          description: descriptionMarkdown,
          photoURLs: Array.isArray(data.photoURLs) ? data.photoURLs : [],
          services: normalizeServices(data.services),
          weeklyHours: normalizeWeeklyHours(data.weeklyHours),
          prices: normalizePrices(data.prices),
          ageRange: Array.isArray(data.ageRange) ? data.ageRange : [],
          tags: Array.isArray(data.tags) ? data.tags : [],
          slogans: Array.isArray(data.slogans) ? data.slogans : [],
          promotion: typeof data.promotion === 'boolean' ? data.promotion : false,
          rating: typeof data.rating === 'number' ? data.rating : 0,
          comments: Array.isArray(data.comments) ? data.comments : [],
          features: Array.isArray(data.features) ? data.features : [],
          theme: data.theme || '',
          guides: Array.isArray(data.guides) ? data.guides : []
        }]);
      } else {
        const snap = await fires.collection('events').orderBy('timestamp', 'desc').limit(100).get();
        const items = snap.docs.map(d => {
          const data = d.data() || {};
          const descriptionMarkdown = data.description || '';
          return {
            id: d.id,
            title: data.title || data.eventDetails?.title || 'Untitled',
            description: descriptionMarkdown,
            photoURLs: Array.isArray(data.photoURLs) ? data.photoURLs : [],
            services: normalizeServices(data.services),
            weeklyHours: normalizeWeeklyHours(data.weeklyHours),
            prices: normalizePrices(data.prices),
            ageRange: Array.isArray(data.ageRange) ? data.ageRange : [],
            tags: Array.isArray(data.tags) ? data.tags : [],
            slogans: Array.isArray(data.slogans) ? data.slogans : [],
            promotion: typeof data.promotion === 'boolean' ? data.promotion : false,
            rating: typeof data.rating === 'number' ? data.rating : 0,
            comments: Array.isArray(data.comments) ? data.comments : [],
            features: Array.isArray(data.features) ? data.features : [],
            theme: data.theme || '',
            guides: Array.isArray(data.guides) ? data.guides : []
          };
        });
        setBusinesses(items);
      }
    } catch (e) {
      console.error('Load businesses failed', e);
      setError('Failed to load businesses');
    }
  };

  const handleAddService = (idx, payload) => {
    const newService = {
      id: `${Date.now()}`,
      name: (payload?.name || '').trim(),
      images: (payload?.images || []).filter(Boolean)
    };
    if (!newService.name) return;
    setBusinesses(prev => prev.map((b, i) => i === idx ? ({ ...b, services: [...(b.services || []), newService] }) : b));
  };

  const handleRemoveService = (idx, serviceId) => {
    setBusinesses(prev => prev.map((b, i) => i === idx ? ({ ...b, services: (b.services || []).filter(s => s.id !== serviceId) }) : b));
  };

  const handleServiceFieldChange = (idx, serviceId, field, value) => {
    setBusinesses(prev => prev.map((b, i) => {
      if (i !== idx) return b;
      const updated = (b.services || []).map(s => s.id === serviceId ? ({
        ...s,
        [field]: field === 'images' ? String(value).split(',').map(v => v.trim()).filter(Boolean)
                 : value
      }) : s);
      return { ...b, services: updated };
    }));
  };

  const handleAddServiceImage = (idx, serviceId, imageUrl) => {
    const url = String(imageUrl || '').trim();
    if (!url) return;
    setBusinesses(prev => prev.map((b, i) => {
      if (i !== idx) return b;
      const updated = (b.services || []).map(s => s.id === serviceId ? ({
        ...s,
        images: [...(s.images || []), url]
      }) : s);
      return { ...b, services: updated };
    }));
  };

  const handleUploadServiceImage = async (idx, serviceId, file) => {
    if (!file) return;
    
    setUploadingImageId(`${idx}-${serviceId}`);
    try {
      const response = await fetch('https://v1serviceapiuploadfile-650318705789.us-central1.run.app', {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': file.type
        },
        body: file
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.downloadURL) {
        handleAddServiceImage(idx, serviceId, data.downloadURL);
      } else {
        throw new Error('No downloadURL in response');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploadingImageId("");
    }
  };

  const handleUploadMultipleServiceImages = async (idx, serviceId, files) => {
    if (!files || files.length === 0) return;
    
    setUploadingImageId(`${idx}-${serviceId}`);
    const uploadPromises = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      uploadPromises.push(
        fetch('https://v1serviceapiuploadfile-650318705789.us-central1.run.app', {
          method: 'POST',
          headers: {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': file.type
          },
          body: file
        }).then(async (response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} for file: ${file.name}`);
          }
          const data = await response.json();
          if (data.downloadURL) {
            return data.downloadURL;
          } else {
            throw new Error(`No downloadURL in response for file: ${file.name}`);
          }
        })
      );
    }
    
    try {
      const downloadURLs = await Promise.all(uploadPromises);
      // Add all successful uploads to the service
      downloadURLs.forEach(url => {
        handleAddServiceImage(idx, serviceId, url);
      });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploadingImageId("");
    }
  };

  const handleRemoveServiceImage = (idx, serviceId, imageUrl) => {
    setBusinesses(prev => prev.map((b, i) => {
      if (i !== idx) return b;
      const updated = (b.services || []).map(s => s.id === serviceId ? ({
        ...s,
        images: (s.images || []).filter(u => u !== imageUrl)
      }) : s);
      return { ...b, services: updated };
    }));
  };

  const handleWeeklyHoursChange = (idx, day, hoursValue) => {
    setBusinesses(prev => prev.map((b, i) => i === idx ? ({
      ...b,
      weeklyHours: b.weeklyHours.map(row => row.day === day ? ({ ...row, hours: hoursValue }) : row)
    }) : b));
  };

  const handlePriceFeeChange = (idx, priceId, feeType, value) => {
    setBusinesses(prev => prev.map((b, i) => {
      if (i !== idx) return b;
      const updated = (b.prices || []).map(p => p.id === priceId ? ({
        ...p,
        fees: {
          ...p.fees,
          [feeType]: parseFloat(value) || 0
        }
      }) : p);
      return { ...b, prices: updated };
    }));
  };

  const handlePriceImageChange = (idx, priceId, imageURL) => {
    setBusinesses(prev => prev.map((b, i) => {
      if (i !== idx) return b;
      const updated = (b.prices || []).map(p => p.id === priceId ? ({
        ...p,
        imageURL: imageURL || ''
      }) : p);
      return { ...b, prices: updated };
    }));
  };

  const handleUploadPriceImage = async (idx, priceId, file) => {
    if (!file) return;
    
    setUploadingImageId(`price-${idx}-${priceId}`);
    try {
      const response = await fetch('https://v1serviceapiuploadfile-650318705789.us-central1.run.app', {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': file.type
        },
        body: file
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.downloadURL) {
        handlePriceImageChange(idx, priceId, data.downloadURL);
      } else {
        throw new Error('No downloadURL in response');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploadingImageId("");
    }
  };

  const handleAddComment = (idx) => {
    const newComment = {
      id: `${Date.now()}`,
      title: '',
      description: '',
      userName: '',
      profileURL: '',
      linkURL: '',
      source: ''
    };
    setBusinesses(prev => prev.map((b, i) => 
      i === idx ? ({ ...b, comments: [...(b.comments || []), newComment] }) : b
    ));
  };

  const handleRemoveComment = (idx, commentId) => {
    setBusinesses(prev => prev.map((b, i) => 
      i === idx ? ({ ...b, comments: (b.comments || []).filter(c => c.id !== commentId) }) : b
    ));
  };

  const handleCommentFieldChange = (idx, commentId, field, value) => {
    setBusinesses(prev => prev.map((b, i) => {
      if (i !== idx) return b;
      const updated = (b.comments || []).map(c => c.id === commentId ? ({
        ...c,
        [field]: value
      }) : c);
      return { ...b, comments: updated };
    }));
  };

  const handleUploadCommentProfileImage = async (idx, commentId, file) => {
    if (!file) return;
    
    setUploadingImageId(`comment-${idx}-${commentId}`);
    try {
      const response = await fetch('https://v1serviceapiuploadfile-650318705789.us-central1.run.app', {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': file.type
        },
        body: file
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.downloadURL) {
        handleCommentFieldChange(idx, commentId, 'profileURL', data.downloadURL);
      } else {
        throw new Error('No downloadURL in response');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploadingImageId("");
    }
  };

  const handleAddGuide = (idx) => {
    const newGuide = {
      id: `${Date.now()}`,
      imageURL: '',
      linkURL: '',
      content: '',
      name: '',
      profileURL: ''
    };
    setBusinesses(prev => prev.map((b, i) => 
      i === idx ? ({ ...b, guides: [...(b.guides || []), newGuide] }) : b
    ));
  };

  const handleRemoveGuide = (idx, guideId) => {
    setBusinesses(prev => prev.map((b, i) => 
      i === idx ? ({ ...b, guides: (b.guides || []).filter(g => g.id !== guideId) }) : b
    ));
  };

  const handleGuideFieldChange = (idx, guideId, field, value) => {
    setBusinesses(prev => prev.map((b, i) => {
      if (i !== idx) return b;
      const updated = (b.guides || []).map(g => g.id === guideId ? ({
        ...g,
        [field]: value
      }) : g);
      return { ...b, guides: updated };
    }));
  };

  const handleUploadGuideImage = async (idx, guideId, file) => {
    if (!file) return;
    
    setUploadingImageId(`guide-image-${idx}-${guideId}`);
    try {
      const response = await fetch('https://v1serviceapiuploadfile-650318705789.us-central1.run.app', {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': file.type
        },
        body: file
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.downloadURL) {
        handleGuideFieldChange(idx, guideId, 'imageURL', data.downloadURL);
      } else {
        throw new Error('No downloadURL in response');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploadingImageId("");
    }
  };

  const handleUploadGuideProfileImage = async (idx, guideId, file) => {
    if (!file) return;
    
    setUploadingImageId(`guide-profile-${idx}-${guideId}`);
    try {
      const response = await fetch('https://v1serviceapiuploadfile-650318705789.us-central1.run.app', {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': file.type
        },
        body: file
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.downloadURL) {
        handleGuideFieldChange(idx, guideId, 'profileURL', data.downloadURL);
      } else {
        throw new Error('No downloadURL in response');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploadingImageId("");
    }
  };

  const generateFeatureId = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  const handleAddFeature = (idx) => {
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const newFeature = {
      id: tempId,
      name: ''
    };
    setBusinesses(prev => prev.map((b, i) => 
      i === idx ? ({ ...b, features: [...(b.features || []), newFeature] }) : b
    ));
  };

  const handleRemoveFeature = (idx, featureId) => {
    setBusinesses(prev => prev.map((b, i) => 
      i === idx ? ({ ...b, features: (b.features || []).filter(f => f.id !== featureId) }) : b
    ));
  };

  const handleFeatureNameChange = (idx, featureId, name) => {
    setBusinesses(prev => prev.map((b, i) => {
      if (i !== idx) return b;
      const updated = (b.features || []).map(f => f.id === featureId ? ({
        ...f,
        name: name
      }) : f);
      return { ...b, features: updated };
    }));
  };

  const handleSaveFeature = (idx, featureId) => {
    setBusinesses(prev => prev.map((b, i) => {
      if (i !== idx) return b;
      const updated = (b.features || []).map(f => {
        if (f.id === featureId && f.name && f.name.trim()) {
          const generatedId = generateFeatureId(f.name);
          return {
            ...f,
            id: generatedId || f.id
          };
        }
        return f;
      });
      return { ...b, features: updated };
    }));
  };

  const handleBusinessFieldChange = (idx, field, value) => {
    setBusinesses(prev => prev.map((b, i) => 
      i === idx ? ({ ...b, [field]: value }) : b
    ));
  };

  const handleDescriptionChange = (idx, value) => {
    setBusinesses(prev => prev.map((b, i) => 
      i === idx ? ({ ...b, description: value || '' }) : b
    ));
  };

  const handleAddPhotoURL = (idx, imageUrl) => {
    const url = String(imageUrl || '').trim();
    if (!url) return;
    setBusinesses(prev => prev.map((b, i) => 
      i === idx ? ({ ...b, photoURLs: [...(b.photoURLs || []), url] }) : b
    ));
  };

  const handleRemovePhotoURL = (idx, imageUrl) => {
    setBusinesses(prev => prev.map((b, i) => 
      i === idx ? ({ ...b, photoURLs: (b.photoURLs || []).filter(u => u !== imageUrl) }) : b
    ));
  };

  const handleReorderPhotoURLs = (idx, dragIndex, dropIndex) => {
    setBusinesses(prev => prev.map((b, i) => {
      if (i !== idx) return b;
      const newPhotoURLs = [...(b.photoURLs || [])];
      const [removed] = newPhotoURLs.splice(dragIndex, 1);
      newPhotoURLs.splice(dropIndex, 0, removed);
      return { ...b, photoURLs: newPhotoURLs };
    }));
  };

  const handleUploadPhoto = async (idx, file) => {
    if (!file) return;
    
    setUploadingImageId(`photo-${idx}`);
    try {
      const response = await fetch('https://v1serviceapiuploadfile-650318705789.us-central1.run.app', {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Content-Type': file.type
        },
        body: file
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.downloadURL) {
        handleAddPhotoURL(idx, data.downloadURL);
      } else {
        throw new Error('No downloadURL in response');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploadingImageId("");
    }
  };

  const handleUploadMultiplePhotos = async (idx, files) => {
    if (!files || files.length === 0) return;
    
    setUploadingImageId(`photo-${idx}`);
    const uploadPromises = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      uploadPromises.push(
        fetch('https://v1serviceapiuploadfile-650318705789.us-central1.run.app', {
          method: 'POST',
          headers: {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': file.type
          },
          body: file
        }).then(async (response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} for file: ${file.name}`);
          }
          const data = await response.json();
          if (data.downloadURL) {
            return data.downloadURL;
          } else {
            throw new Error(`No downloadURL in response for file: ${file.name}`);
          }
        })
      );
    }
    
    try {
      const downloadURLs = await Promise.all(uploadPromises);
      // Add all successful uploads
      downloadURLs.forEach(url => {
        handleAddPhotoURL(idx, url);
      });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploadingImageId("");
    }
  };

  const handleSave = async (biz) => {
    try {
      setSavingId(biz.id);
      
      // Get the current document to preserve existing data structure
      const doc = await fires.collection('events').doc(biz.id).get();
      const currentData = doc.data() || {};
      const currentPrices = currentData.prices || {};
      
      // Update only the fees and imageURL for existing prices, preserving all other fields
      const updatedPrices = { ...currentPrices };
      (biz.prices || []).forEach(price => {
        if (updatedPrices[price.id]) {
          // Preserve existing structure and update fees and imageURL
          updatedPrices[price.id] = {
            ...updatedPrices[price.id],
            fees: {
              ...updatedPrices[price.id].fees,
              ...price.fees
            },
            imageURL: typeof price.imageURL === 'string' ? price.imageURL : ''
          };
        }
      });
      
      // Filter out features without names (incomplete features)
      const validFeatures = (biz.features || []).filter(f => f.name && f.name.trim() && f.id && !f.id.startsWith('temp-'));
      
      // Get description (markdown format)
      const descriptionMarkdown = (biz.description || '').trim();
      
      // Prepare update data
      const updateData = {
        title: typeof biz.title === 'string' ? biz.title : '',
        description: descriptionMarkdown,
        detailDescription: descriptionMarkdown,
        photoURLs: Array.isArray(biz.photoURLs) ? biz.photoURLs : [],
        services: biz.services || [],
        weeklyHours: Array.isArray(biz.weeklyHours) ? biz.weeklyHours : defaultWeeklyHours,
        prices: updatedPrices,
        ageRange: Array.isArray(biz.ageRange) ? biz.ageRange : [],
        tags: Array.isArray(biz.tags) ? biz.tags : [],
        slogans: Array.isArray(biz.slogans) ? biz.slogans : [],
        promotion: typeof biz.promotion === 'boolean' ? biz.promotion : false,
        rating: typeof biz.rating === 'number' && biz.rating >= 0 && biz.rating <= 5 ? biz.rating : 0,
        comments: Array.isArray(biz.comments) ? biz.comments : [],
        features: validFeatures,
        theme: typeof biz.theme === 'string' ? biz.theme : '',
        guides: Array.isArray(biz.guides) ? biz.guides : []
      };
      
      // Track changes for changelog
      const changes = {};
      const fieldsToTrack = [
        'title', 'description', 'photoURLs', 'services', 'weeklyHours', 
        'prices', 'ageRange', 'tags', 'slogans', 'promotion', 'rating', 
        'comments', 'features', 'theme', 'guides'
      ];
      
      fieldsToTrack.forEach(field => {
        const oldValue = currentData[field];
        const newValue = updateData[field];
        
        // Deep comparison for arrays and objects
        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          changes[field] = {
            old: oldValue,
            new: newValue
          };
        }
      });
      
      // Update the document
      await fires.collection('events').doc(biz.id).update(updateData);
      
      // Create changelog entry if there are changes
      if (Object.keys(changes).length > 0) {
        const changelogData = {
          timestamp: new Date(),
          changedBy: currentUser?.uid || 'unknown',
          changedByEmail: currentUser?.email || 'unknown',
          changes: changes,
          summary: Object.keys(changes).join(', ')
        };
        
        await fires
          .collection('events')
          .doc(biz.id)
          .collection('changelogs')
          .add(changelogData);
      }
    } catch (e) {
      console.error('Save business failed', e);
      alert('Failed to save: ' + (e?.message || 'Unknown error'));
    } finally {
      setSavingId("");
    }
  };

  const filtered = businesses.filter(b => {
    if (!query) return true;
    const q = query.toLowerCase();
    return b.title.toLowerCase().includes(q) || b.id.toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="py-5 text-center">
        <h5>Unauthorized</h5>
        <p className="text-muted">You do not have access to this page.</p>
      </Container>
    );
  }

  const renderSingle = (biz, idx) => (
    <Container fluid className="py-4">
      <Row className="mb-3" style={{ position: 'sticky', top: '70px', zIndex: 100, backgroundColor: 'white', paddingTop: '1rem', paddingBottom: '1rem', borderBottom: '2px solid #dee2e6', marginTop: '-1rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0">{biz.title}</h4>
              <small className="text-muted">ID: <code>{biz.id}</code></small>
            </div>
            <div>
              <Button
                variant="primary"
                onClick={() => handleSave(biz)}
                disabled={savingId === biz.id}
              >
                {savingId === biz.id ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3"><Col><div className="text-danger small">{error}</div></Col></Row>
      )}

      <Row className="g-3">
        <Col md={4}>
          <Card className="shadow-sm">
            <Card.Header>
              <h6 className="mb-0">Opening Hours</h6>
            </Card.Header>
            <Card.Body>
              <Table size="sm" bordered responsive className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 140 }}>Day</th>
                    <th>Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {biz.weeklyHours.map(row => (
                    <tr key={row.day}>
                      <td>{row.day}</td>
                      <td>
                        <Form.Control
                          type="text"
                          placeholder="e.g. 11:00 AM - 2:00 AM (Next day) or Closed"
                          value={row.hours}
                          onChange={(e) => handleWeeklyHoursChange(idx, row.day, e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header>
              <h6 className="mb-0">Price Management</h6>
            </Card.Header>
            <Card.Body>
              {(biz.prices || []).length === 0 ? (
                <div className="text-muted small mb-2">No prices configured</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <Table size="sm" bordered responsive>
                    <thead>
                      <tr>
                        <th style={{minWidth: 300}}>Name</th>
                        <th style={{minWidth: 150}}>Image</th>
                        <th style={{width: 120}}>Original</th>
                        <th style={{width: 120}}>Display</th>
                        <th style={{width: 120}}>Discount</th>
                        <th style={{minWidth: 150}}>Tax</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(biz.prices || []).map(price => (
                        <tr key={price.id}>
                          <td style={{ minWidth: '300px', maxWidth: '500px', verticalAlign: 'middle' }}>
                            <div
                              style={{ 
                                padding: '6px 12px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                minWidth: '280px',
                                width: '100%',
                                whiteSpace: 'normal',
                                wordWrap: 'break-word',
                                overflow: 'visible',
                                lineHeight: '1.5'
                              }}
                            >
                              {price.name || 'Untitled'}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-2">
                              {price.imageURL && (
                                <div className="position-relative" style={{ width: '100px', height: '100px' }}>
                                  <img
                                    src={price.imageURL}
                                    alt="Price"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                                    onError={(e) => { e.target.style.display='none'; }}
                                  />
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    className="position-absolute top-0 end-0 m-1"
                                    onClick={() => handlePriceImageChange(idx, price.id, '')}
                                    style={{ fontSize: '10px', padding: '2px 4px' }}
                                  >
                                    ×
                                  </Button>
                                </div>
                              )}
                              <div className="d-flex flex-column gap-1">
                                <Form.Control
                                  size="sm"
                                  type="url"
                                  placeholder="Image URL"
                                  value={price.imageURL || ''}
                                  onChange={(e) => handlePriceImageChange(idx, price.id, e.target.value)}
                                  style={{ fontSize: '11px' }}
                                />
                                <div className="text-center">
                                  <small className="text-muted">or</small>
                                </div>
                                <Form.Control
                                  size="sm"
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleUploadPriceImage(idx, price.id, file);
                                      e.target.value = '';
                                    }
                                  }}
                                  disabled={uploadingImageId === `price-${idx}-${price.id}`}
                                  style={{ fontSize: '11px' }}
                                />
                                {(biz.photoURLs || []).length > 0 && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      onClick={() => setShowPhotoSelector({ [`${idx}-${price.id}`]: true })}
                                      style={{ fontSize: '11px', width: '100%' }}
                                    >
                                      Select from photos
                                    </Button>
                                    <Modal
                                      show={showPhotoSelector[`${idx}-${price.id}`] || false}
                                      onHide={() => setShowPhotoSelector({ ...showPhotoSelector, [`${idx}-${price.id}`]: false })}
                                      size="lg"
                                    >
                                      <Modal.Header closeButton>
                                        <Modal.Title>Select Photo</Modal.Title>
                                      </Modal.Header>
                                      <Modal.Body>
                                        <div className="d-flex flex-wrap gap-3">
                                          {(biz.photoURLs || []).map((url, urlIdx) => (
                                            <div
                                              key={urlIdx}
                                              style={{
                                                width: '150px',
                                                height: '150px',
                                                cursor: 'pointer',
                                                border: '2px solid #dee2e6',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                transition: 'all 0.2s'
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.borderColor = '#007bff';
                                                e.currentTarget.style.transform = 'scale(1.05)';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.borderColor = '#dee2e6';
                                                e.currentTarget.style.transform = 'scale(1)';
                                              }}
                                              onClick={() => {
                                                handlePriceImageChange(idx, price.id, url);
                                                setShowPhotoSelector({ ...showPhotoSelector, [`${idx}-${price.id}`]: false });
                                              }}
                                            >
                                              <img
                                                src={url}
                                                alt={`Photo ${urlIdx + 1}`}
                                                style={{
                                                  width: '100%',
                                                  height: '100%',
                                                  objectFit: 'cover'
                                                }}
                                                onError={(e) => {
                                                  e.target.style.display = 'none';
                                                  e.target.parentElement.style.backgroundColor = '#f8f9fa';
                                                  e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #6c757d;">Invalid Image</div>';
                                                }}
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      </Modal.Body>
                                    </Modal>
                                  </>
                                )}
                                {uploadingImageId === `price-${idx}-${price.id}` && (
                                  <Spinner size="sm" className="mx-auto" />
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              type="number"
                              step="0.01"
                              min="0"
                              value={price.fees?.original || 0}
                              onChange={(e) => handlePriceFeeChange(idx, price.id, 'original', e.target.value)}
                              placeholder="0.00"
                              style={{ minWidth: '100px' }}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              type="number"
                              step="0.01"
                              min="0"
                              value={price.fees?.display || 0}
                              onChange={(e) => handlePriceFeeChange(idx, price.id, 'display', e.target.value)}
                              placeholder="0.00"
                              style={{ minWidth: '100px' }}
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              type="number"
                              step="0.01"
                              min="0"
                              value={price.fees?.discount || 0}
                              onChange={(e) => handlePriceFeeChange(idx, price.id, 'discount', e.target.value)}
                              placeholder="0.00"
                              style={{ minWidth: '100px' }}
                            />
                          </td>
                          <td style={{ minWidth: '150px' }}>
                            <Form.Control
                              size="sm"
                              type="number"
                              step="0.01"
                              min="0"
                              value={price.fees?.tax || 0}
                              onChange={(e) => handlePriceFeeChange(idx, price.id, 'tax', e.target.value)}
                              placeholder="0.00"
                              style={{ minWidth: '140px', width: '100%' }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header>
              <h6 className="mb-0">Services</h6>
            </Card.Header>
            <Card.Body>
              {(biz.services || []).length === 0 ? (
                <div className="text-muted small mb-2">No services</div>
              ) : (
                <>
                  <Table size="sm" bordered responsive>
                    <thead>
                      <tr>
                        <th style={{minWidth: 200}}>Name</th>
                        <th style={{width: 100}}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(biz.services || []).map(svc => (
                        <tr key={svc.id}>
                          <td>
                            <Form.Control
                              value={svc.name}
                              onChange={(e) => handleServiceFieldChange(idx, svc.id, 'name', e.target.value)}
                            />
                          </td>
                          <td className="text-center">
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleRemoveService(idx, svc.id)}
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {(biz.services || []).map(svc => (
                    <div key={`img-${svc.id}`} className="mb-3">
                      <div className="fw-bold small mb-1">Images for: {svc.name}</div>
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {(svc.images || []).length === 0 && (
                          <span className="text-muted small">No images</span>
                        )}
                        {(svc.images || []).map(url => (
                          <div key={url} className="border rounded p-1" style={{ width: 96 }}>
                            <div className="ratio ratio-1x1 mb-1" style={{ backgroundColor: '#f8f9fa' }}>
                              <img src={url} alt="" style={{ objectFit: 'cover' }} onError={(e) => { e.target.style.display='none'; }} />
                            </div>
                            <Button size="sm" variant="outline-danger" className="w-100" onClick={() => handleRemoveServiceImage(idx, svc.id, url)}>Remove</Button>
                          </div>
                        ))}
                      </div>
                      <Form.Group>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = e.target.files;
                            if (files && files.length > 0) {
                              if (files.length === 1) {
                                handleUploadServiceImage(idx, svc.id, files[0]);
                              } else {
                                handleUploadMultipleServiceImages(idx, svc.id, Array.from(files));
                              }
                            }
                          }}
                          disabled={uploadingImageId === `${idx}-${svc.id}`}
                        />
                        {uploadingImageId === `${idx}-${svc.id}` && (
                          <small className="text-muted">Uploading...</small>
                        )}
                        <Form.Text className="text-muted">
                          Select one or multiple images to upload
                        </Form.Text>
                      </Form.Group>
                    </div>
                  ))}
                </>
              )}

              <div className="mt-3">
                <h6 className="mb-2">Add New Service</h6>
                <Row className="g-2">
                  <Col sm={8}>
                    <Form.Control placeholder="Service Name" id={`svc-name-${biz.id}`} />
                  </Col>
                  <Col sm={4}>
                    <Button
                      variant="outline-primary"
                      onClick={() => {
                        const name = document.getElementById(`svc-name-${biz.id}`)?.value?.trim();
                        if (!name) return;
                        handleAddService(idx, {
                          name,
                          type: 'image',
                          images: []
                        });
                        const el = document.getElementById(`svc-name-${biz.id}`);
                        if (el) el.value = '';
                      }}
                    >Add Service</Button>
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col md={12}>
          <Card className="shadow-sm">
            <Card.Header>
              <h6 className="mb-0">Age Range</h6>
            </Card.Header>
            <Card.Body>
              <div>
                <Form.Check
                  type="checkbox"
                  id={`ageRange-0-3-${biz.id}`}
                  label="0 - 3"
                  checked={(biz.ageRange || []).includes('0-3')}
                  onChange={(e) => {
                    const currentRanges = biz.ageRange || [];
                    const newRanges = e.target.checked
                      ? [...currentRanges, '0-3'].filter((v, i, a) => a.indexOf(v) === i) // Remove duplicates
                      : currentRanges.filter(r => r !== '0-3');
                    setBusinesses(prev => prev.map((b, i) => i === idx ? ({ ...b, ageRange: newRanges }) : b));
                  }}
                />
                <Form.Check
                  type="checkbox"
                  id={`ageRange-3-6-${biz.id}`}
                  label="3 - 6"
                  checked={(biz.ageRange || []).includes('3-6')}
                  onChange={(e) => {
                    const currentRanges = biz.ageRange || [];
                    const newRanges = e.target.checked
                      ? [...currentRanges, '3-6'].filter((v, i, a) => a.indexOf(v) === i) // Remove duplicates
                      : currentRanges.filter(r => r !== '3-6');
                    setBusinesses(prev => prev.map((b, i) => i === idx ? ({ ...b, ageRange: newRanges }) : b));
                  }}
                />
                <Form.Check
                  type="checkbox"
                  id={`ageRange-6plus-${biz.id}`}
                  label="6+"
                  checked={(biz.ageRange || []).includes('6+')}
                  onChange={(e) => {
                    const currentRanges = biz.ageRange || [];
                    const newRanges = e.target.checked
                      ? [...currentRanges, '6+'].filter((v, i, a) => a.indexOf(v) === i) // Remove duplicates
                      : currentRanges.filter(r => r !== '6+');
                    setBusinesses(prev => prev.map((b, i) => i === idx ? ({ ...b, ageRange: newRanges }) : b));
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col md={12}>
          <Card className="shadow-sm">
            <Card.Header>
              <h6 className="mb-0">Theme</h6>
            </Card.Header>
            <Card.Body>
              <Form.Group>
                <Form.Label>Select Theme</Form.Label>
                <Form.Select
                  value={biz.theme || ''}
                  onChange={(e) => {
                    setBusinesses(prev => prev.map((b, i) => 
                      i === idx ? ({ ...b, theme: e.target.value }) : b
                    ));
                  }}
                >
                  <option value="">Select a theme</option>
                  <option value="Playground">Playground</option>
                  <option value="Sports">Sports</option>
                  <option value="Arts">Arts</option>
                  <option value="Arcade">Arcade</option>
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col md={12}>
          <Card className="shadow-sm">
            <Card.Header>
              <h6 className="mb-0">Tags</h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {(biz.tags || []).length === 0 ? (
                    <span className="text-muted small">No tags added</span>
                  ) : (
                    (biz.tags || []).map((tag, tagIdx) => (
                      <Badge
                        key={tagIdx}
                        bg="primary"
                        className="d-inline-flex align-items-center gap-1"
                        style={{ fontSize: '0.9rem', padding: '0.5rem' }}
                      >
                        {tag}
                        <Button
                          variant="link"
                          className="p-0 text-white"
                          style={{ fontSize: '0.8rem', textDecoration: 'none', lineHeight: 1 }}
                          onClick={() => {
                            const newTags = (biz.tags || []).filter((_, i) => i !== tagIdx);
                            setBusinesses(prev => prev.map((b, i) => i === idx ? ({ ...b, tags: newTags }) : b));
                          }}
                        >
                          ×
                        </Button>
                      </Badge>
                    ))
                  )}
                </div>
                <Row className="g-2">
                  <Col sm={8}>
                    <Form.Control
                      type="text"
                      placeholder="Enter a tag and press Enter or click Add"
                      id={`tag-input-${biz.id}`}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const input = document.getElementById(`tag-input-${biz.id}`);
                          const tagValue = input?.value?.trim();
                          if (tagValue && !(biz.tags || []).includes(tagValue)) {
                            setBusinesses(prev => prev.map((b, i) => 
                              i === idx ? ({ ...b, tags: [...(b.tags || []), tagValue] }) : b
                            ));
                            if (input) input.value = '';
                          }
                        }
                      }}
                    />
                  </Col>
                  <Col sm={4}>
                    <Button
                      variant="outline-primary"
                      onClick={() => {
                        const input = document.getElementById(`tag-input-${biz.id}`);
                        const tagValue = input?.value?.trim();
                        if (tagValue && !(biz.tags || []).includes(tagValue)) {
                          setBusinesses(prev => prev.map((b, i) => 
                            i === idx ? ({ ...b, tags: [...(b.tags || []), tagValue] }) : b
                          ));
                          if (input) input.value = '';
                        }
                      }}
                    >
                      Add Tag
                    </Button>
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col md={12}>
          <Card className="shadow-sm">
            <Card.Header>
              <h6 className="mb-0">Slogans</h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {(biz.slogans || []).length === 0 ? (
                    <span className="text-muted small">No slogans added</span>
                  ) : (
                    (biz.slogans || []).map((slogan, sloganIdx) => (
                      <Badge
                        key={sloganIdx}
                        bg="secondary"
                        className="d-inline-flex align-items-center gap-1"
                        style={{ fontSize: '0.9rem', padding: '0.5rem' }}
                      >
                        {slogan}
                        <Button
                          variant="link"
                          className="p-0 text-white"
                          style={{ fontSize: '0.8rem', textDecoration: 'none', lineHeight: 1 }}
                          onClick={() => {
                            const newSlogans = (biz.slogans || []).filter((_, i) => i !== sloganIdx);
                            setBusinesses(prev => prev.map((b, i) => i === idx ? ({ ...b, slogans: newSlogans }) : b));
                          }}
                        >
                          ×
                        </Button>
                      </Badge>
                    ))
                  )}
                </div>
                <Row className="g-2">
                  <Col sm={8}>
                    <Form.Control
                      type="text"
                      placeholder="Enter a slogan and press Enter or click Add"
                      id={`slogan-input-${biz.id}`}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const input = document.getElementById(`slogan-input-${biz.id}`);
                          const sloganValue = input?.value?.trim();
                          if (sloganValue && !(biz.slogans || []).includes(sloganValue)) {
                            setBusinesses(prev => prev.map((b, i) => 
                              i === idx ? ({ ...b, slogans: [...(b.slogans || []), sloganValue] }) : b
                            ));
                            if (input) input.value = '';
                          }
                        }
                      }}
                    />
                  </Col>
                  <Col sm={4}>
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        const input = document.getElementById(`slogan-input-${biz.id}`);
                        const sloganValue = input?.value?.trim();
                        if (sloganValue && !(biz.slogans || []).includes(sloganValue)) {
                          setBusinesses(prev => prev.map((b, i) => 
                            i === idx ? ({ ...b, slogans: [...(b.slogans || []), sloganValue] }) : b
                          ));
                          if (input) input.value = '';
                        }
                      }}
                    >
                      Add Slogan
                    </Button>
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col md={12}>
          <Card className="shadow-sm">
            <Card.Header>
              <h6 className="mb-0">Promotion</h6>
            </Card.Header>
            <Card.Body>
              <Form.Check
                type="checkbox"
                id={`promotion-${biz.id}`}
                label="Enable Promotion"
                checked={biz.promotion === true}
                onChange={(e) => {
                  setBusinesses(prev => prev.map((b, i) => 
                    i === idx ? ({ ...b, promotion: e.target.checked }) : b
                  ));
                }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col md={12}>
          <Card className="shadow-sm">
            <Card.Header>
              <h6 className="mb-0">Rating</h6>
            </Card.Header>
            <Card.Body>
              <Form.Group>
                <Form.Label>Rating (0 to 5)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={biz.rating || 0}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    const clampedValue = Math.min(5, Math.max(0, value));
                    setBusinesses(prev => prev.map((b, i) => 
                      i === idx ? ({ ...b, rating: clampedValue }) : b
                    ));
                  }}
                  placeholder="Enter rating (0-5)"
                />
                <Form.Text className="text-muted">
                  Enter a rating between 0 and 5 (e.g., 4.5, 4.6, 4.7)
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col md={12}>
          <Card className="shadow-sm">
            <Card.Header>
              <h6 className="mb-0">Title, Description & Photos</h6>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={biz.title || ''}
                      onChange={(e) => handleBusinessFieldChange(idx, 'title', e.target.value)}
                      placeholder="Enter business title"
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Description (Markdown)</Form.Label>
                    <div data-color-mode="light" style={{ minHeight: '300px' }}>
                      <MDEditor
                        value={biz.description || ''}
                        onChange={(value) => handleDescriptionChange(idx, value)}
                        preview="edit"
                        hideToolbar={false}
                        visibleDragBar={true}
                        height={300}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Use Markdown syntax for formatting. Supports headings, lists, links, images, and more.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Photo URLs</Form.Label>
                    {(biz.photoURLs || []).length > 0 && (
                      <div className="mb-3">
                        <div className="d-flex flex-wrap gap-2">
                          {(biz.photoURLs || []).map((url, urlIdx) => (
                            <div 
                              key={urlIdx} 
                              className="position-relative" 
                              style={{ 
                                width: '150px', 
                                height: '150px',
                                cursor: 'move',
                                opacity: draggedPhotoIndex === urlIdx && draggedPhotoBusinessId === biz.id ? 0.5 : 1,
                                border: draggedPhotoIndex === urlIdx && draggedPhotoBusinessId === biz.id ? '2px dashed #007bff' : 'none'
                              }}
                              draggable
                              onDragStart={(e) => {
                                setDraggedPhotoIndex(urlIdx);
                                setDraggedPhotoBusinessId(biz.id);
                                e.dataTransfer.effectAllowed = 'move';
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                if (draggedPhotoIndex !== null && draggedPhotoBusinessId === biz.id && draggedPhotoIndex !== urlIdx) {
                                  handleReorderPhotoURLs(idx, draggedPhotoIndex, urlIdx);
                                }
                                setDraggedPhotoIndex(null);
                                setDraggedPhotoBusinessId(null);
                              }}
                              onDragEnd={() => {
                                setDraggedPhotoIndex(null);
                                setDraggedPhotoBusinessId(null);
                              }}
                            >
                              <img
                                src={url}
                                alt={`Photo ${urlIdx + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px', pointerEvents: 'none' }}
                                onError={(e) => { e.target.style.display='none'; }}
                                draggable={false}
                              />
                              <div 
                                className="position-absolute top-0 start-0 m-1"
                                style={{ 
                                  backgroundColor: 'rgba(0, 0, 0, 0.6)', 
                                  color: 'white', 
                                  padding: '2px 6px', 
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  pointerEvents: 'none'
                                }}
                              >
                                ⋮⋮
                              </div>
                              <Button
                                size="sm"
                                variant="danger"
                                className="position-absolute top-0 end-0 m-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemovePhotoURL(idx, url);
                                }}
                                style={{ fontSize: '12px', padding: '2px 6px' }}
                              >
                                ×
                              </Button>
                            </div>
                          ))}
                        </div>
                        <small className="text-muted d-block mt-2">
                          <i className="fas fa-info-circle me-1"></i>
                          Drag photos to reorder them
                        </small>
                      </div>
                    )}
                    <InputGroup className="mb-2">
                      <Form.Control
                        type="url"
                        placeholder="Enter photo URL"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const url = e.target.value.trim();
                            if (url) {
                              handleAddPhotoURL(idx, url);
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={(e) => {
                          const input = e.target.closest('.input-group').querySelector('input');
                          const url = input.value.trim();
                          if (url) {
                            handleAddPhotoURL(idx, url);
                            input.value = '';
                          }
                        }}
                      >
                        Add URL
                      </Button>
                    </InputGroup>
                    <div className="text-center mb-2">
                      <small className="text-muted">or</small>
                    </div>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleUploadPhoto(idx, file);
                            e.target.value = '';
                          }
                        }}
                        disabled={uploadingImageId === `photo-${idx}`}
                      />
                      <Form.Control
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length > 0) {
                            handleUploadMultiplePhotos(idx, files);
                            e.target.value = '';
                          }
                        }}
                        disabled={uploadingImageId === `photo-${idx}`}
                      />
                      <Button
                        variant="outline-secondary"
                        disabled={uploadingImageId === `photo-${idx}`}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.multiple = true;
                          input.onchange = (e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length > 0) {
                              handleUploadMultiplePhotos(idx, files);
                            }
                          };
                          input.click();
                        }}
                      >
                        Upload Multiple
                      </Button>
                    </div>
                    {uploadingImageId === `photo-${idx}` && (
                      <div className="mt-2">
                        <Spinner size="sm" animation="border" className="me-2" />
                        <small className="text-muted">Uploading...</small>
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col md={12}>
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Comments</h6>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => handleAddComment(idx)}
              >
                Add Comment
              </Button>
            </Card.Header>
            <Card.Body>
              {(biz.comments || []).length === 0 ? (
                <div className="text-muted small">No comments added</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {(biz.comments || []).map((comment, commentIdx) => (
                    <Card key={comment.id} className="border">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <h6 className="mb-0">Comment #{commentIdx + 1}</h6>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleRemoveComment(idx, comment.id)}
                          >
                            Remove
                          </Button>
                        </div>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Title</Form.Label>
                              <Form.Control
                                type="text"
                                value={comment.title || ''}
                                onChange={(e) => handleCommentFieldChange(idx, comment.id, 'title', e.target.value)}
                                placeholder="Enter comment title"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>User Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={comment.userName || ''}
                                onChange={(e) => handleCommentFieldChange(idx, comment.id, 'userName', e.target.value)}
                                placeholder="Enter user name"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group>
                              <Form.Label>Description</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={comment.description || ''}
                                onChange={(e) => handleCommentFieldChange(idx, comment.id, 'description', e.target.value)}
                                placeholder="Enter comment description"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Profile URL</Form.Label>
                              <div className="mb-2">
                                {comment.profileURL ? (
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <img 
                                      src={comment.profileURL} 
                                      alt="Profile" 
                                      style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '50%' }}
                                      onError={(e) => { e.target.style.display='none'; }}
                                    />
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => handleCommentFieldChange(idx, comment.id, 'profileURL', '')}
                                    >
                                      Remove
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="text-muted small mb-2">No profile image</div>
                                )}
                              </div>
                              <Form.Control
                                type="url"
                                value={comment.profileURL || ''}
                                onChange={(e) => handleCommentFieldChange(idx, comment.id, 'profileURL', e.target.value)}
                                placeholder="Enter profile image URL"
                                className="mb-2"
                              />
                              <div className="text-center mb-2">
                                <small className="text-muted">or</small>
                              </div>
                              <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleUploadCommentProfileImage(idx, comment.id, file);
                                  }
                                }}
                                disabled={uploadingImageId === `comment-${idx}-${comment.id}`}
                              />
                              {uploadingImageId === `comment-${idx}-${comment.id}` && (
                                <small className="text-muted">Uploading...</small>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Link URL</Form.Label>
                              <Form.Control
                                type="url"
                                value={comment.linkURL || ''}
                                onChange={(e) => handleCommentFieldChange(idx, comment.id, 'linkURL', e.target.value)}
                                placeholder="Enter link URL"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Source</Form.Label>
                              <Form.Control
                                type="text"
                                value={comment.source || ''}
                                onChange={(e) => handleCommentFieldChange(idx, comment.id, 'source', e.target.value)}
                                placeholder="Enter source (e.g., Google, Yelp, Facebook)"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col md={12}>
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Guides</h6>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => handleAddGuide(idx)}
              >
                Add Guide
              </Button>
            </Card.Header>
            <Card.Body>
              {(biz.guides || []).length === 0 ? (
                <div className="text-muted small">No guides added</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {(biz.guides || []).map((guide, guideIdx) => (
                    <Card key={guide.id} className="border">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <h6 className="mb-0">Guide #{guideIdx + 1}</h6>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleRemoveGuide(idx, guide.id)}
                          >
                            Remove
                          </Button>
                        </div>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={guide.name || ''}
                                onChange={(e) => handleGuideFieldChange(idx, guide.id, 'name', e.target.value)}
                                placeholder="Enter guide name"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Link URL</Form.Label>
                              <Form.Control
                                type="url"
                                value={guide.linkURL || ''}
                                onChange={(e) => handleGuideFieldChange(idx, guide.id, 'linkURL', e.target.value)}
                                placeholder="Enter link URL"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group>
                              <Form.Label>Content</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={guide.content || ''}
                                onChange={(e) => handleGuideFieldChange(idx, guide.id, 'content', e.target.value)}
                                placeholder="Enter guide content"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Image URL</Form.Label>
                              <div className="mb-2">
                                {guide.imageURL ? (
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <img 
                                      src={guide.imageURL} 
                                      alt="Guide" 
                                      style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                                      onError={(e) => { e.target.style.display='none'; }}
                                    />
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => handleGuideFieldChange(idx, guide.id, 'imageURL', '')}
                                    >
                                      Remove
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="text-muted small mb-2">No image</div>
                                )}
                              </div>
                              <Form.Control
                                type="url"
                                value={guide.imageURL || ''}
                                onChange={(e) => handleGuideFieldChange(idx, guide.id, 'imageURL', e.target.value)}
                                placeholder="Enter image URL"
                                className="mb-2"
                              />
                              <div className="text-center mb-2">
                                <small className="text-muted">or</small>
                              </div>
                              <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleUploadGuideImage(idx, guide.id, file);
                                  }
                                }}
                                disabled={uploadingImageId === `guide-image-${idx}-${guide.id}`}
                              />
                              {uploadingImageId === `guide-image-${idx}-${guide.id}` && (
                                <small className="text-muted">Uploading...</small>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Profile URL (Optional)</Form.Label>
                              <div className="mb-2">
                                {guide.profileURL ? (
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <img 
                                      src={guide.profileURL} 
                                      alt="Profile" 
                                      style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '50%' }}
                                      onError={(e) => { e.target.style.display='none'; }}
                                    />
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => handleGuideFieldChange(idx, guide.id, 'profileURL', '')}
                                    >
                                      Remove
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="text-muted small mb-2">No profile image</div>
                                )}
                              </div>
                              <Form.Control
                                type="url"
                                value={guide.profileURL || ''}
                                onChange={(e) => handleGuideFieldChange(idx, guide.id, 'profileURL', e.target.value)}
                                placeholder="Enter profile image URL"
                                className="mb-2"
                              />
                              <div className="text-center mb-2">
                                <small className="text-muted">or</small>
                              </div>
                              <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleUploadGuideProfileImage(idx, guide.id, file);
                                  }
                                }}
                                disabled={uploadingImageId === `guide-profile-${idx}-${guide.id}`}
                              />
                              {uploadingImageId === `guide-profile-${idx}-${guide.id}` && (
                                <small className="text-muted">Uploading...</small>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col md={12}>
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Features</h6>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => handleAddFeature(idx)}
              >
                Add Feature
              </Button>
            </Card.Header>
            <Card.Body>
              {(biz.features || []).length === 0 ? (
                <div className="text-muted small">No features added</div>
              ) : (
                <Table responsive striped bordered>
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>Name</th>
                      <th style={{ width: '40%' }}>ID (Auto-generated)</th>
                      <th style={{ width: '20%' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(biz.features || []).map((feature) => {
                      const isSaved = feature.id && !feature.id.startsWith('temp-');
                      const hasName = feature.name && feature.name.trim();
                      return (
                        <tr key={feature.id || `feature-${Date.now()}`}>
                          <td>
                            <Form.Control
                              type="text"
                              value={feature.name || ''}
                              onChange={(e) => handleFeatureNameChange(idx, feature.id, e.target.value)}
                              placeholder="Enter feature name"
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              value={isSaved ? feature.id : ''}
                              readOnly
                              className="bg-light"
                              placeholder={isSaved ? "ID generated" : "Click Save to generate ID"}
                            />
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant={isSaved ? "success" : "primary"}
                                onClick={() => handleSaveFeature(idx, feature.id)}
                                disabled={!hasName}
                              >
                                {isSaved ? 'Saved' : 'Save'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleRemoveFeature(idx, feature.id)}
                              >
                                Remove
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );

  if (eventId && businesses.length === 1) {
    return renderSingle(businesses[0], 0);
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0">Business Management</h4>
              <small className="text-muted">Manage services and weekly hours for businesses</small>
            </div>
            <div className="d-flex gap-2 align-items-center">
              {!eventId && (
                <InputGroup style={{ maxWidth: 320 }}>
                  <InputGroup.Text>Search</InputGroup.Text>
                  <Form.Control value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Title or ID" />
                </InputGroup>
              )}
              <Button variant="outline-secondary" onClick={loadBusinesses}>{eventId ? 'Reload Business' : 'Reload'}</Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3"><Col><div className="text-danger small">{error}</div></Col></Row>
      )}

      <Row className="g-3">
        {filtered.map((biz, idx) => (
          <Col key={biz.id} xs={12} lg={6}>
            <Card className="shadow-sm">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="fw-bold">{biz.title}</div>
                  <div className="text-muted small"><code>{biz.id}</code></div>
                </div>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => handleSave(biz)}
                  disabled={savingId === biz.id}
                >
                  {savingId === biz.id ? 'Saving...' : 'Save'}
                </Button>
              </Card.Header>
              <Card.Body>
                {/* Re-use same editors as single view */}
                {renderSingle(biz, idx)}
              </Card.Body>
            </Card>
          </Col>
        ))}
        {filtered.length === 0 && (
          <Col xs={12} className="text-center text-muted py-5">No businesses found</Col>
        )}
      </Row>
    </Container>
  );
}


